import styled from 'styled-components';
import Review1 from '../../../assets/images/pluvaAutoLanding/review1.gif';
import Review2 from '../../../assets/images/pluvaAutoLanding/review2.gif';
import Review3 from '../../../assets/images/pluvaAutoLanding/review3.gif';
import ReviewObject from '../../../assets/images/pluvaAutoLanding/review_object.jpg';
import { Icon } from '../../../components/common/icon/Icon';
import { YoutubeSection } from '../../../components/common/video/YoutubeSection';
import { PointBadge } from './PointBadge';

interface IReview {
  summary: string;
  primaryUseAndLocation: string;
  youtubeUrl?: string;
  gifUrl?: string;
  name: string;
}

const reviews: IReview[] = [
  {
    summary: '플루바 오토 달아서 야간 작업을 했는데 야간인데도 골이 잘 잡힌거 같아요.',
    primaryUseAndLocation: '전라남도 무안, 고구마 농사',
    gifUrl: Review1,
    name: '강행원',
  },
  {
    summary: '이앙기 운전은 플루바 오토에 맡기고\n저는 뒤에서 모판 교체해요.',
    primaryUseAndLocation: '경상북도 상주, 논/콩/축사',
    gifUrl: Review2,
    name: '민인규',
  },
  {
    summary: '다른분한테 추천해요. 이거 무조건 달아라.때려서라도 달게 해야 될 정도로 좋으니까.',
    primaryUseAndLocation: '충청남도 당진, 논 농사',
    gifUrl: Review3,
    name: '이순재',
  },
  {
    summary: '원래 10시간 하던거 7시간이면 끝나요.',
    primaryUseAndLocation: '전라북도 익산, 축사/조사료',
    youtubeUrl: 'https://youtu.be/L51-SqDqVH8',
    name: '이지훈',
  },
  {
    summary: '예상했던 것보다 4두둑이나 더 나왔어요.',
    primaryUseAndLocation: '충청남도 아산, 콩/벼',
    youtubeUrl: 'https://youtu.be/KVOec1ZM3eI',
    name: '권호진',
  },
];

const ReviewBox = ({ review }: { review: IReview }) => {
  const starCount = 5;
  const { summary, primaryUseAndLocation, youtubeUrl, gifUrl, name } = review;
  return (
    <ReviewContainer>
      {gifUrl && <ReviewGif src={gifUrl} />}
      {youtubeUrl && <YoutubeSection youtubeUrl={youtubeUrl} />}
      <ReviewContentSection>
        <ReviewSummaryBox>
          <ReviewInfoSummaryText>{summary}</ReviewInfoSummaryText>

          <UserInfo>
            <PrimaryUseAndLocationText>{primaryUseAndLocation}</PrimaryUseAndLocationText>

            <UserNameAndStartRatingWrapper>
              <UserName>{`${name} 고객님`}</UserName>
              <StarRatingBox>
                <StarRating>
                  {Array.from({ length: starCount }, (_, index) => (
                    <Icon key={index} name={'Star'} width={20} height={20} />
                  ))}
                </StarRating>
                <StarCount>{starCount.toFixed(1)}</StarCount>
              </StarRatingBox>
            </UserNameAndStartRatingWrapper>
          </UserInfo>
        </ReviewSummaryBox>
      </ReviewContentSection>
    </ReviewContainer>
  );
};

export const CustomerReviewSection = () => {
  return (
    <Container>
      <TitleWrapper>
        <PointBadge title={'리뷰 보기'} />
        <Title>{'플루바 오토를 경험한\n고객님들의 실제 리뷰 영상!'}</Title>
        <Image src={ReviewObject} />
        <SubTitle>{'고객님들이 직접 경험한 놀라운 편의성,\n만족도는 상승! 수익은 쑥쑥!'}</SubTitle>
      </TitleWrapper>
      <ReviewGroup>
        {reviews.map((review: IReview) => {
          return <ReviewBox key={review.summary} review={review} />;
        })}
      </ReviewGroup>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  padding: 48px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const Title = styled.h2`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/XXX-Large/xxxl-bold */
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 31.2px */

  white-space: pre-line;
`;

const SubTitle = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */

  white-space: pre-line;
`;

const ReviewGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  align-self: stretch;
`;

const ReviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  overflow: hidden;

  border-radius: var(--radius-medium, 8px);
`;

const ReviewGif = styled.img`
  border-radius: var(--radius-none, 0px);
  background: var(--Light-Scheme-Monochrome-black, #232429);
`;

const ReviewContentSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--radius-small, 4px);
  align-self: stretch;
`;

const PrimaryUseAndLocationText = styled.div`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */
`;

const ReviewSummaryBox = styled.div`
  display: flex;
  padding: 24px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;

  border-radius: var(--radius-none, 0px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const ReviewInfoSummaryText = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--radius-small, 4px);
  align-self: stretch;
`;

const UserNameAndStartRatingWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--radius-medium, 8px);
  align-self: stretch;
`;

const UserName = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 18.2px */
`;

const StarRatingBox = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--radius-small, 4px);
  flex: 1 0 0;
  align-self: stretch;
`;

const StarRating = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StarCount = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */
`;

const Image = styled.img`
  width: 180px;
`;
